import React from "react";
import { CAPTURE_CHAT_TEXT, captureConfirmBlockedHint, captureConfirmHint } from "../../lib/captureChat";
import EventLineFields from "../EventLineFields/EventLineFields";
import "./CaptureConfirmPicker.css";

function canEditItem(item, done, saving) {
  if (done || saving || item.duplicate) return false;
  return Boolean(item.selectable || !item.date);
}

/**
 * Multi-event KiTa-like confirm picker in chat.
 * Title and date stay on the line until Merken stores them.
 */
export default function CaptureConfirmPicker({
  items,
  saving = false,
  done = false,
  onToggle,
  onToggleChild,
  onEdit,
  onConfirm,
  onCancel,
}) {
  const selectableCount = (items || []).filter((i) => i.selectable).length;
  const selectedCount = (items || []).filter((i) => i.selected && i.selectable).length;

  return (
    <div className="capture-confirm-picker" role="group" aria-label="Termine prüfen">
      <ul className="capture-confirm-picker-list">
        {(items || []).map((item, idx) => {
          const hint = captureConfirmHint(item);
          const editable = canEditItem(item, done, saving);
          return (
            <li key={`${item.eventIndex}-${idx}`}>
              <div
                className={`capture-confirm-picker-row${editable ? "" : " capture-confirm-picker-row--disabled"}`}
              >
                <input
                  type="checkbox"
                  aria-label={item.title ? `${item.title} merken` : "Termin merken"}
                  checked={!!item.selected}
                  disabled={done || saving || !item.selectable}
                  onChange={() => onToggle?.(idx)}
                />
                <span className="capture-confirm-picker-label">
                  <EventLineFields
                    title={item.title}
                    date={item.date}
                    endDate={item.end_date || ""}
                    time={item.time}
                    allDay={item.all_day !== false && !item.time}
                    editable={editable}
                    disabled={saving}
                    onCommit={(patch) => onEdit?.(idx, patch)}
                  />
                  {item.place ? (
                    <span className="capture-confirm-picker-place">{item.place}</span>
                  ) : null}
                  {hint ? (
                    <span className="capture-confirm-picker-hint-line">{hint}</span>
                  ) : null}
                  {item.date && !item.selectable && selectableCount > 0 ? (
                    <span className="capture-confirm-picker-hint-line">{CAPTURE_CHAT_TEXT.alreadySaved}</span>
                  ) : null}
                </span>
              </div>
              {item.selectable && Array.isArray(item.childChoices) && item.childChoices.length >= 2 ? (
                <div className="capture-confirm-kids" role="group" aria-label="Für welche Kinder">
                  {item.childChoices.map((child) => {
                    const on = (item.selectedChildIds || []).includes(child.id);
                    return (
                      <button
                        key={child.id}
                        type="button"
                        className={`capture-confirm-kid${on ? " capture-confirm-kid--on" : ""}`}
                        aria-pressed={on}
                        disabled={done || saving}
                        onClick={() => onToggleChild?.(idx, child.id)}
                      >
                        {child.name}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {!done ? (
        <div className="capture-confirm-picker-actions">
          <button
            type="button"
            className="chat-action-btn"
            disabled={saving || selectedCount === 0}
            onClick={onConfirm}
          >
            {saving ? "Speichert…" : `Merken${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
          </button>
          <button
            type="button"
            className="chat-action-btn capture-confirm-cancel"
            disabled={saving}
            onClick={onCancel}
          >
            Abbrechen
          </button>
        </div>
      ) : null}
      {selectableCount === 0 && captureConfirmBlockedHint(items) ? (
        <p className="capture-confirm-picker-hint">{captureConfirmBlockedHint(items)}</p>
      ) : null}
    </div>
  );
}
